import { c as _c } from "react/compiler-runtime";
import { GroupItem } from "common/types/graphql";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import { Check, Loader2 } from "lucide-react";
import clsx from "classnames";
import { parseFrequency } from "modules/newsletters/utils/parseFrequency";
import { Thumbnail } from "../Thumbnail";
export interface NewsletterCardProps {
  newsletter: GroupItem;
  selected: boolean;
  loading: boolean;
  isSubscribed: boolean;
  onToggle: () => void;
  regionCode: string;
}

/**
 * Presentational newsletter card component for v2 design.
 * Displays newsletter information and handles user interactions via callbacks.
 */
export const NewsletterCard = t0 => {
  "use memo";

  const $ = _c(48);
  const {
    newsletter,
    selected,
    loading,
    isSubscribed,
    onToggle,
    regionCode
  } = t0;
  const t = useTranslations("newsletters");
  let t1;
  if ($[0] !== newsletter.frequency) {
    t1 = parseFrequency(newsletter.frequency);
    $[0] = newsletter.frequency;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsedFrequency = t1;
  const t2 = selected && !loading;
  const t3 = selected && !isSubscribed;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = clsx("relative flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-neutral-50 text-left transition-colors md:flex-row", "hover:border-gray-400 hover:bg-white", "focus-within:ring-2 focus-within:ring-black focus-within:ring-offset-2", {
      "bg-white": t2,
      "!border-black ring-1 ring-black": t3
    });
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = selected ? "button.unsubscribe_from" : "button.subscribe_to";
  let t6;
  if ($[5] !== newsletter.name || $[6] !== t || $[7] !== t5) {
    t6 = t(t5, {
      name: newsletter.name
    });
    $[5] = newsletter.name;
    $[6] = t;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== loading || $[10] !== onToggle || $[11] !== selected || $[12] !== t6) {
    t7 = <button onClick={onToggle} disabled={loading} aria-busy={loading} aria-pressed={selected} aria-label={t6} className="absolute inset-0 z-0 h-full w-full cursor-pointer outline-none" />;
    $[9] = loading;
    $[10] = onToggle;
    $[11] = selected;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = selected && !isSubscribed;
  const t9 = !selected;
  const t10 = selected && isSubscribed && !loading;
  let t11;
  if ($[14] !== t10 || $[15] !== t8 || $[16] !== t9) {
    t11 = clsx("relative flex h-10 w-10 items-center justify-center rounded-full border border-gray-200", {
      "!border-black bg-black text-white": t8,
      "group-hover:border-gray-400": t9,
      "border-none bg-green-300 text-black": t10
    });
    $[14] = t10;
    $[15] = t8;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== loading || $[19] !== selected) {
    t12 = loading ? <Loader2 className="size-5 motion-safe:animate-spin" /> : selected ? <Check className="size-6" strokeWidth={1.5} /> : <Check className="hidden size-6 text-gray-600 group-focus-within:block group-hover:block" strokeWidth={1.5} />;
    $[18] = loading;
    $[19] = selected;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11 || $[22] !== t12) {
    t13 = <div className="pointer-events-none relative ps-6 pe-0 pt-6"><div className={t11} data-testid="newsletter-card-selection">{t12}</div></div>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== newsletter.name) {
    t14 = <div className="relative inline-flex items-center gap-2"><h3 className="text-xl font-medium">{newsletter.name}</h3></div>;
    $[24] = newsletter.name;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== newsletter.pitch) {
    t15 = <p className="text-md leading-6">{newsletter.pitch}</p>;
    $[26] = newsletter.pitch;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== parsedFrequency.type) {
    t16 = <span className="text-sm text-red-400">{parsedFrequency.type}</span>;
    $[28] = parsedFrequency.type;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== parsedFrequency.details) {
    t17 = parsedFrequency.details && <><span className="text-gray-600">•</span><span className="text-gray-600">{parsedFrequency.details}</span></>;
    $[30] = parsedFrequency.details;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t16 || $[33] !== t17) {
    t18 = <div className="mt-2 flex flex-wrap gap-2 text-sm">{t16}{t17}</div>;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t18) {
    t19 = <div className="pointer-events-none relative min-w-0 flex-1 space-y-1 p-6 pt-4 sm:pt-6">{t14}{t15}{t18}</div>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== newsletter.name || $[40] !== regionCode) {
    t20 = <Thumbnail regionCode={regionCode} newsletterName={newsletter.name} className="pointer-events-none absolute top-0 right-0 h-20 w-20 flex-shrink-0 overflow-hidden rounded-bl-2xl bg-cover bg-center bg-no-repeat md:static md:h-auto md:w-36 md:rounded-none" />;
    $[39] = newsletter.name;
    $[40] = regionCode;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t13 || $[43] !== t19 || $[44] !== t20 || $[45] !== t4 || $[46] !== t7) {
    t21 = <div className="group relative"><div className={t4} data-testid="newsletter-card">{t7}{t13}{t19}{t20}</div></div>;
    $[42] = t13;
    $[43] = t19;
    $[44] = t20;
    $[45] = t4;
    $[46] = t7;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
};